/**
 * Live TLB usage: resolve TSR unit / label / engine context → totalCarriedForward keys.
 * Keep DEFAULT_LIVE_TLB_USAGE_MAPPING aligned with utils/liveTlbUsageMapping.js (server).
 */

/** @type {{ byUnit: Record<string, string|null>, engineHoursByPosition: { LH: string, RH: string, default: string }, labelRules: Array<{ unit: string, label: string, tlbKey: string }> }} */
export const DEFAULT_LIVE_TLB_USAGE_MAPPING = {
  byUnit: {
    Hours: 'airframeHours',
    Landings: 'landings',
    Years: null,
    'Calendar Days': null,
    Cycles: 'lhEngineCyclesCT',
    'Hobbs Meter': 'hobbs',
    'Hobbs Meter AC': 'hobbsAC'
  },
  engineHoursByPosition: {
    LH: 'engine1Hours',
    RH: 'engine2Hours',
    default: 'engine1Hours'
  },
  labelRules: []
};

export function normalizeTsrLabel(s) {
  return String(s || '').trim().toLowerCase();
}

/**
 * @param {object|null|undefined} effectiveMapping - from API effectiveTlbUsageMapping or merged local
 * @param {{ unit?: string, label?: string, tlbCarriedForwardKey?: string, assemblyRole?: string, position?: string }} ctx
 * @returns {{ key: string|null, reason?: string }}
 */
export function resolveLiveTlbValuesKey(effectiveMapping, ctx) {
  const unit = String(ctx?.unit || '').trim();
  if (!unit) {
    return { key: null, reason: 'Missing TSR unit.' };
  }

  const explicitKey = ctx?.tlbCarriedForwardKey != null ? String(ctx.tlbCarriedForwardKey).trim() : '';
  if (explicitKey) {
    return { key: explicitKey };
  }

  const map = effectiveMapping && typeof effectiveMapping === 'object' ? effectiveMapping : {};
  const byUnit = { ...DEFAULT_LIVE_TLB_USAGE_MAPPING.byUnit, ...(map.byUnit || {}) };
  const engineHoursByPosition = {
    ...DEFAULT_LIVE_TLB_USAGE_MAPPING.engineHoursByPosition,
    ...(map.engineHoursByPosition || {})
  };
  const labelRules = Array.isArray(map.labelRules) && map.labelRules.length > 0
    ? map.labelRules
    : DEFAULT_LIVE_TLB_USAGE_MAPPING.labelRules;

  const assemblyRole = String(ctx?.assemblyRole || '').trim();
  if (unit === 'Hours' && assemblyRole === 'ENGINE') {
    const pos = String(ctx?.position || '').trim().toUpperCase();
    const keyName = pos === 'LH' ? engineHoursByPosition.LH : pos === 'RH' ? engineHoursByPosition.RH : engineHoursByPosition.default;
    if (!keyName || String(keyName).trim() === '') {
      return {
        key: null,
        reason: 'No TLB field mapped for engine hours (LH/RH). Configure Client Settings → Live aircraft usage (TLB).'
      };
    }
    return { key: String(keyName).trim() };
  }

  const normLabel = normalizeTsrLabel(ctx?.label);
  if (normLabel) {
    for (let i = 0; i < labelRules.length; i++) {
      const r = labelRules[i];
      if (!r) continue;
      if (String(r.unit || '').trim() !== unit) continue;
      if (normalizeTsrLabel(r.label) === normLabel && r.tlbKey) {
        return { key: String(r.tlbKey).trim() };
      }
    }
  }

  const k = byUnit[unit];
  if (k == null || k === '') {
    return {
      key: null,
      reason: `No TLB totalCarriedForward field mapped for unit "${unit}". Configure Client Settings → Live aircraft usage (TLB), or add a label rule for this TSR.`
    };
  }
  return { key: String(k).trim() };
}

/** Build context for getLiveRawForTsr from a time-since record. */
export function liveTsrContextFromRecord(record, extra = {}) {
  const unit =
    record?.elapsedTime?.unit ||
    record?.limit?.unit ||
    record?.remainingTime?.unit ||
    '';
  return {
    unit,
    label: record?.label,
    tlbCarriedForwardKey: record?.tlbCarriedForwardKey,
    ...extra
  };
}

/**
 * @param {object|null|undefined} values - currentUsageTotals.values
 * @param {object|null|undefined} effectiveMapping
 * @param {{ unit?: string, label?: string, tlbCarriedForwardKey?: string, assemblyRole?: string, position?: string }} ctx
 */
export function getLiveRawForTsr(values, effectiveMapping, ctx) {
  const { key, reason } = resolveLiveTlbValuesKey(effectiveMapping, ctx);
  if (!key) {
    return { raw: null, key: null, reason };
  }
  if (!values || typeof values !== 'object') {
    return { raw: null, key, reason: 'Live/current aircraft usage totals are not available.' };
  }
  const raw = values[key];
  if (raw == null) {
    return {
      raw: null,
      key,
      reason: `Latest approved TLB totals have no value for field "${key}".`
    };
  }
  return { raw, key, reason: undefined };
}
